import React, { useEffect, useState } from 'react';

import { climbBridgeAPI } from '../../../lib/api';

const KINDS = [
  { value: '', label: 'Everything' },
  { value: 'leaderboard', label: 'Leaderboard' },
  { value: 'performances', label: 'Performances' },
  { value: 'setup', label: 'Setup' },
  { value: 'poll-error', label: 'Errors only' },
];

/**
 * Screen 3 — Diagnostics.
 *
 * The question this screen exists to answer in under a minute: **is the number
 * on screen wrong because ClimbAlong is not moving, or because the bridge is
 * reading it wrong?** The raw log answers the first half and the poll-to-poll
 * diff answers the second, and having both side by side is what makes the answer
 * fast rather than a debugging session.
 */
export default function DiagnosticsPanel({ summary, showId }) {
  const [kind, setKind] = useState('');
  const [log, setLog] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!showId) return undefined;
    let cancelled = false;
    const load = () => climbBridgeAPI.upstream(showId, { limit: 20, ...(kind ? { kind } : {}) })
      .then(res => { if (!cancelled) setLog(res.data); })
      .catch(err => { if (!cancelled) setError(err.response?.data?.error || ''); });
    load();
    const timer = setInterval(load, 3000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [showId, kind]);

  const drift = summary?.health?.schemaDrift || [];
  const warnings = summary?.health?.warnings || [];
  const diff = log?.diff;

  return (
    <div className="space-y-5">
      {error && <div className="px-4 py-3 bg-red-900/30 border border-red-700 rounded text-red-400 text-sm">{error}</div>}

      {/* Schema drift — its own alarm, deliberately not folded into "warnings" */}
      {drift.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold text-amber-400 mb-2">Schema drift</h2>
          <div className="px-4 py-3 bg-amber-900/20 border border-amber-700 rounded space-y-1.5">
            <p className="text-[11px] text-amber-300/80 leading-relaxed">
              ClimbAlong sent a shape the bridge did not expect. Rows that still parsed were published; these did not.
              This is its own alarm rather than a generic warning because the alternative — a field quietly rendering as
              blank on air — is exactly what it exists to prevent.
            </p>
            {drift.map((d, i) => (
              <div key={i} className="text-xs text-amber-400 font-mono">
                <span className="text-amber-500/70">{d.label}{d.index != null ? `[${d.index}]` : ''}</span> {d.message}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Data warnings */}
      {warnings.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold text-white mb-2">Warnings</h2>
          <div className="space-y-1">
            {warnings.slice().reverse().map((w, i) => (
              <div key={i} className="flex items-start gap-2 px-3 py-1.5 bg-[#111111] border border-[#2a2a2a] rounded text-xs">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-500 w-32 flex-shrink-0">{w.type}</span>
                <span className="text-gray-300 flex-1">{w.message}</span>
                {w.count > 1 && <span className="text-gray-600 tabular-nums">×{w.count}</span>}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Poll-to-poll diff */}
      <section>
        <h2 className="text-sm font-semibold text-white mb-2">What changed between the last two polls</h2>
        {diff?.available ? (
          <div className="border border-[#2a2a2a] rounded overflow-hidden">
            <div className="px-3 py-1.5 bg-[#1a1a1a] text-[10px] text-gray-500">
              {diff.kind} · {new Date(diff.from).toLocaleTimeString()} → {new Date(diff.to).toLocaleTimeString()} · {diff.changes.length} field(s)
            </div>
            {diff.changes.length === 0 ? (
              <div className="px-3 py-3 text-xs text-gray-500">
                Nothing changed. If a graphic looks stuck, this is your answer — the upstream is not moving, and that is not a bridge fault.
              </div>
            ) : (
              <div className="max-h-64 overflow-auto">
                {diff.changes.map((c, i) => (
                  <div key={i} className="px-3 py-1 border-t border-[#2a2a2a] text-[11px] font-mono flex gap-2">
                    <span className="text-gray-500 flex-1 truncate">{c.path}</span>
                    <span className="text-red-400/70">{JSON.stringify(c.from)}</span>
                    <span className="text-gray-700">→</span>
                    <span className="text-green-400/80">{JSON.stringify(c.to)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="px-3 py-3 bg-[#111111] border border-[#2a2a2a] rounded text-xs text-gray-500">
            Needs two polls of the same endpoint before there is anything to compare.
          </div>
        )}
      </section>

      {/* Raw log */}
      <section>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-semibold text-white">Raw upstream responses</h2>
          <select
            value={kind}
            onChange={e => setKind(e.target.value)}
            className="bg-[#0a0a0a] border border-[#2a2a2a] rounded px-2 py-1 text-xs text-gray-300 focus:outline-none focus:border-blue-500"
          >
            {KINDS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
          </select>
        </div>

        <div className="border border-[#2a2a2a] rounded overflow-hidden">
          {(log?.entries || []).length === 0 ? (
            <div className="px-3 py-4 text-xs text-gray-500">No responses captured yet.</div>
          ) : (
            log.entries.map(entry => (
              <div key={entry.seq} className="border-t border-[#2a2a2a] first:border-t-0">
                <button
                  onClick={() => setExpanded(expanded === entry.seq ? null : entry.seq)}
                  className="w-full flex items-center gap-3 px-3 py-1.5 hover:bg-[#151515] text-left text-[11px] font-mono"
                >
                  <span className="text-gray-600 tabular-nums w-20">{new Date(entry.ts).toLocaleTimeString()}</span>
                  <span className={`w-24 ${entry.error ? 'text-red-400' : 'text-gray-400'}`}>{entry.kind}</span>
                  <span className={`w-12 tabular-nums ${entry.error ? 'text-red-400' : 'text-green-400'}`}>{entry.status ?? 'ERR'}</span>
                  <span className="text-gray-600 tabular-nums w-16">{entry.ms != null ? `${entry.ms}ms` : ''}</span>
                  <span className="text-gray-600 truncate flex-1">{entry.error || entry.path}</span>
                </button>
                {expanded === entry.seq && (
                  <pre className="px-3 py-2 bg-[#0a0a0a] text-[10px] text-gray-400 overflow-auto max-h-80">
                    {JSON.stringify(entry.data, null, 2)}
                  </pre>
                )}
              </div>
            ))
          )}
        </div>
      </section>

    </div>
  );
}
